<template>
  <VContainer fluid class="pa-0" :style="{ '--nav-bottom': navBottom + 'px' }">
    <!-- Dims the page under the drawer and closes it on tap, as nuxt2's temporary
         drawer did. Vuetify's own drawer scrim is absolutely positioned in the
         drawer's parent (a header column here), so it could not cover the page. -->
    <HeaderScrim class="nav-drawer-scrim" data-testid="nav-drawer-scrim" @close="emits('close')" />
    <!-- Vuetify closes the drawer itself on a swipe or a breakpoint change. Forward
         that, so the parent unmounts the drawer, the scrim and the scroll lock together
         instead of leaving the page dimmed and locked with no drawer on screen. -->
    <VNavigationDrawer
      :model-value="true"
      location="start"
      :scrim="false"
      :z-index="DRAWER_Z_INDEX"
      class="nav-drawer"
      @update:model-value="open => { if (!open) emits('close') }"
    >
      <VCard class="menu-content pa-0" variant="flat">
        <AccountUserMenu mobile-menu />
        <template v-if="showLoyaltyRow">
          <VRow class="py-3" no-gutters>
            <VCol>
              <LoyaltyRewardsInfo />
            </VCol>
          </VRow>
          <VDivider color="backgroundDiv" :opacity="1" />
        </template>
        <LayoutHeaderDemonstratorHeader v-if="showDemonstratorHeader" border />
        <CategoriesMenu
          :base-category="baseCategory"
          mobile
          @close="emits('close')"
        />
        <LayoutHeaderSparkitLogo
          :categories="categories"
          variant="menu"
          test-id="sparkit-logo-mobile"
          @close="emits('close')"
        />
      </VCard>
    </VNavigationDrawer>
  </VContainer>
</template>

<script setup lang="ts">
import type { CategoryItem } from '~/types/root'
import CategoriesMenu from '~/components/layout/header/menu/CategoriesMenu.vue'
import HeaderScrim from '~/components/layout/header/HeaderScrim.vue'
import LoyaltyRewardsInfo from '~/components/base/LoyaltyRewardsInfo.vue'
import { useHeaderTopLinks } from '~/composables/useHeaderTopLinks'

interface Props {
  categories: CategoryItem[]
}

const props = withDefaults(defineProps<Props>(), {
})

const emits = defineEmits<{
  (e: 'close'): void
}>()

// The drawer's layer, mirroring $drawer-z-index in assets/scss/lib/vars.scss (the SCSS
// below uses the variable; this copy is for the script). The scrim sits one below it;
// HeaderLayout raises the header's stacking context to the same value while open.
const DRAWER_Z_INDEX = 2147483645

const headerTopLinks = useHeaderTopLinks()
const { isDemo } = useSAuthState()
const { canSeeRewards, isLoyaltyMember } = useRewardsVisibility()

// nuxt2 MobileMenu: the rewards balance sits under the user menu for any signed-in,
// non-employee user who opted in to loyalty, demonstrators included.
const showLoyaltyRow = computed(() => canSeeRewards.value && isLoyaltyMember.value)
// "Shopping with" / Find a Demonstrator: a demonstrator shops for themself (nuxt2 parity).
const showDemonstratorHeader = computed(() => !isDemo.value)

// The drawer shows the same role-dependent entries as the desktop top-links row
// (single rule: useHeaderTopLinks). MenuItemList already opens `http` slugs in a
// new window, so the descriptor's `external` flag needs no CategoryItem field.
const baseCategory = computed<CategoryItem>(() => {
  const children: CategoryItem[] = [...props.categories]

  children.push(...headerTopLinks.value.map(link => ({
    id: link.id,
    name: link.label,
    slug: link.to
  })))

  return {
    name: "",
    id: "top",
    children
  }
})

// Lock background scroll while the drawer is open (standard mobile pattern). With
// the page unable to scroll, the header can't move, so the drawer simply pins to
// the header's current bottom edge (the divider) — no scroll listener / resize
// observer tracking needed. Anchor it on open and re-read on resize (orientation).
const getNavbar = () => document.querySelector('[data-testid="navbar"]') as HTMLElement | null
const navBottom = ref(0)
const updateNavBottom = () => { navBottom.value = Math.round(getNavbar()?.getBoundingClientRect().bottom ?? 0) }
// The page scroll lock is owned by HeaderLayout (shared with the Events panel).
onMounted(() => {
  updateNavBottom()
  window.addEventListener('resize', updateNavBottom)
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', updateNavBottom)
})
</script>

<style lang="scss" scoped>
@use '~/assets/scss/lib/vars';

.menu-content {
  border-radius: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  position: relative;
  z-index: 200;
}

// HeaderScrim's default tint is nuxt2's Vuetify 2 overlay (#212121 at 0.46). It starts
// below the header and sits inside the header's raised stacking context, one layer
// under the drawer; the z-index only orders the two.
.nav-drawer-scrim {
  top: var(--nav-bottom, 0px);
  z-index: vars.$drawer-scrim-z-index;
}

.nav-drawer {
  width: 85vw !important;
  max-width: 400px;
  // Interactive surface: above scrims, below PWA controls (2147483646).
  z-index: vars.$drawer-z-index !important;
  box-shadow: none !important;
  border-right-width: 0 !important;
  // !important is required here: VNavigationDrawer sets top/height as INLINE
  // styles (e.g. `style="top:0; height:calc(100% + 0px)"`), which a plain scoped
  // rule can't override. Without it the drawer jumps to the viewport top.
  top: var(--nav-bottom, 0px) !important;
  // Fallback for browsers without dvh support; the second declaration overrides
  // on modern browsers where 100dvh accounts for mobile Safari's collapsing URL bar.
  height: calc(100vh - var(--nav-bottom, 0px)) !important;
  height: calc(100dvh - var(--nav-bottom, 0px)) !important;

  .menu-content {
    width: 100% !important;
  }
}
</style>
